import { Head, Link, usePage } from "@inertiajs/react"
import { ArrowLeft, ArrowRight, Check, Copy, Sparkles } from "lucide-react"
import { useCallback, useMemo, useState } from "react"
import camelcaseKeys from "camelcase-keys"
import AudioPlayer from "@/components/conversation/audio-player"
import { SharePopover } from "@/components/share-popover"
import { MarketingNav } from "@/components/marketing-nav"
import { MarketingFooter } from "@/components/marketing-footer"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useClipboard } from "@/hooks/use-clipboard"
import { usePrefetchLink } from "@/hooks/use-prefetch-link"
import { cn } from "@/lib/utils"
import type { WordShowProps } from "@/types/word"

const ROMANIZATION_KEY = "thaicopilot-romanization-preference"

type RomanizationSystem = "paiboon" | "rtgs"

const TONE_ARROWS: Record<string, string> = {
  rising: "\u2197",
  falling: "\u2198",
  mid: "\u2192",
  low: "\u2193",
  high: "\u2191",
}

const TONE_DESCRIPTIONS: Record<string, string> = {
  rising: "your voice rises from low to high",
  falling: "your voice drops from high to low",
  mid: "your voice stays flat at a mid-level pitch",
  low: "your voice stays flat at a low pitch",
  high: "your voice stays flat at a high pitch",
}

function getRomanizationPreference(): RomanizationSystem {
  if (typeof window === "undefined") return "paiboon"
  return (localStorage.getItem(ROMANIZATION_KEY) as RomanizationSystem) || "paiboon"
}

function setRomanizationPreference(system: RomanizationSystem) {
  if (typeof window !== "undefined") {
    localStorage.setItem(ROMANIZATION_KEY, system)
  }
}

export default function WordShow(props: unknown) {
  const page = usePage()
  const { auth } = page.props

  const data = useMemo(
    () => camelcaseKeys(props as Record<string, unknown>, { deep: true }) as unknown as WordShowProps,
    [props]
  )

  const { word, relatedWords = [], seo, canonicalUrl } = data

  const [romanization, setRomanization] = useState<RomanizationSystem>(getRomanizationPreference)
  const [, copyToClipboard] = useClipboard()
  const [copied, setCopied] = useState(false)

  const dashboardPrefetch = usePrefetchLink("/dashboard")
  const signUpPrefetch = usePrefetchLink("/sign_up")
  const howItWorksPrefetch = usePrefetchLink("/how-it-works")

  const shareUrl = typeof window !== "undefined" ? window.location.href : (canonicalUrl ?? "")

  const toggleRomanization = useCallback(() => {
    setRomanization((prev) => {
      const next = prev === "paiboon" ? "rtgs" : "paiboon"
      setRomanizationPreference(next)
      return next
    })
  }, [])

  const heroRomanization = romanization === "paiboon"
    ? word.romanizationPaiboon
    : word.romanizationRtgs

  const handleCopy = useCallback(async () => {
    if (heroRomanization) {
      await copyToClipboard(heroRomanization)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }, [heroRomanization, copyToClipboard])

  const getPhraseRomanization = useCallback(
    (phrase: { romanizationPaiboon?: string; romanization: string }) => {
      if (romanization === "paiboon" && phrase.romanizationPaiboon) {
        return phrase.romanizationPaiboon
      }
      return phrase.romanization
    },
    [romanization]
  )

  return (
    <>
      {seo && (
        <Head title={seo.title}>
          <meta name="description" content={seo.description} />
          <meta name="robots" content="index, follow" />
          {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

          <meta property="og:title" content={seo.title} />
          <meta property="og:description" content={seo.description} />
          <meta property="og:type" content="article" />
          {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
          <meta property="og:image" content={seo.imageUrl} />

          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content={seo.title} />
          <meta name="twitter:description" content={seo.description} />
          <meta name="twitter:image" content={seo.imageUrl} />

          <script type="application/ld+json">
            {JSON.stringify(seo.structuredData)}
          </script>
        </Head>
      )}

      <MarketingNav />

      <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
        {/* Hero */}
        <div className="relative overflow-hidden border-b border-slate-200 bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 dark:border-slate-800 dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/30">
          <div className="pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-indigo-500/5 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-24 size-72 rounded-full bg-violet-500/5 blur-3xl" />

          <div className="relative mx-auto max-w-4xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
            {/* Breadcrumb */}
            <nav className="mb-6 flex items-center gap-2 text-sm text-muted-foreground">
              <Link href="/" className="transition-colors hover:text-indigo-600 dark:hover:text-indigo-400">
                Home
              </Link>
              <span className="text-slate-300 dark:text-slate-600">/</span>
              <Link href="/words" className="transition-colors hover:text-indigo-600 dark:hover:text-indigo-400">
                Words
              </Link>
              <span className="text-slate-300 dark:text-slate-600">/</span>
              <span className="font-medium text-foreground line-clamp-1">{word.english}</span>
            </nav>

            {/* Back link */}
            <Link
              href="/words"
              className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-slate-600 transition-colors hover:text-indigo-600 dark:text-slate-400 dark:hover:text-indigo-400"
            >
              <ArrowLeft className="size-4" />
              All Words
            </Link>

            {/* Hero Card */}
            <Card className="overflow-hidden border-slate-200 bg-white shadow-xl shadow-slate-200/50 dark:border-slate-700 dark:bg-slate-900 dark:shadow-none">
              {/* Header */}
              <div className="border-b border-slate-100 bg-slate-50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/50 sm:px-8">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Badge variant="secondary" className="mb-3 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-300">
                      Essential Word
                    </Badge>
                    <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
                      {word.english}
                    </h1>
                  </div>
                  <SharePopover
                    url={shareUrl}
                    title={`"${word.english}" in Thai`}
                    description={heroRomanization ? `${heroRomanization} — ${word.thaiScript}` : word.thaiScript}
                  />
                </div>
              </div>

              {/* Romanization hero */}
              <CardContent className="p-8 sm:p-12">
                <div className="text-center">
                  {/* SAY IT label */}
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Say it
                  </p>

                  {/* Romanization hero + copy */}
                  {heroRomanization && (
                    <div className="mb-2 flex items-center justify-center gap-3">
                      <p className="text-4xl font-bold text-slate-900 dark:text-white sm:text-5xl lg:text-6xl">
                        {heroRomanization}
                      </p>
                      <button
                        onClick={handleCopy}
                        className="mt-1 rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300"
                        aria-label="Copy romanization"
                      >
                        {copied ? <Check className="size-5 text-green-500" /> : <Copy className="size-5" />}
                      </button>
                    </div>
                  )}

                  {/* Audio */}
                  {word.audioUrl && (
                    <div className="mx-auto mb-4 w-full max-w-md">
                      <AudioPlayer audioUrl={word.audioUrl} variant="dark" className="w-full px-4 py-3" />
                    </div>
                  )}

                  {/* Thai script secondary */}
                  <p className="mb-4 text-2xl text-slate-600 dark:text-slate-400">
                    {word.thaiScript}
                  </p>

                  {/* RTGS / Paiboon toggle */}
                  {word.romanizationRtgs && word.romanizationPaiboon && (
                    <button
                      onClick={toggleRomanization}
                      className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-sm text-muted-foreground transition-colors hover:border-indigo-300 hover:text-indigo-600 dark:border-slate-700 dark:hover:border-indigo-700 dark:hover:text-indigo-400"
                    >
                      {romanization === "paiboon" ? "Paiboon+" : "RTGS"}
                      <span className="text-slate-300 dark:text-slate-600">|</span>
                      <span className="font-medium text-indigo-600 dark:text-indigo-400">
                        Switch to {romanization === "paiboon" ? "RTGS" : "Paiboon+"}
                      </span>
                    </button>
                  )}

                  {/* Badges row */}
                  <TooltipProvider>
                    <div className="flex flex-wrap items-center justify-center gap-2">
                      {word.rank && (
                        <Badge variant="outline" className="text-sm">
                          #{word.rank} most common
                        </Badge>
                      )}
                      {word.partOfSpeech && (
                        <Badge variant="secondary" className="text-sm">
                          {word.partOfSpeech}
                        </Badge>
                      )}
                      {word.tone && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge variant="secondary" className="cursor-help gap-1 text-sm">
                              {TONE_ARROWS[word.tone] || ""} {word.tone} tone
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-xs">
                            <p>
                              Thai has 5 tones. {word.tone.charAt(0).toUpperCase() + word.tone.slice(1)} tone
                              ({TONE_ARROWS[word.tone]}) means {TONE_DESCRIPTIONS[word.tone] || "a specific pitch pattern"}.
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TooltipProvider>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Content sections */}
        <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6 lg:px-8">
          {/* Definition */}
          {word.definition && (
            <Card className="mb-10 border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900">
              <CardContent className="p-6 sm:p-8">
                <h2 className="mb-4 text-xl font-bold text-foreground">
                  What does {word.thaiScript} mean?
                </h2>
                <p className="text-lg leading-relaxed text-slate-600 dark:text-slate-400">
                  {word.definition}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Sample Phrases */}
          {word.samplePhrases?.length > 0 && (
            <div className="mb-10">
              <h2 className="mb-6 text-xl font-bold text-foreground">
                Example Phrases
              </h2>
              <div className="space-y-4">
                {word.samplePhrases.map((phrase, index) => (
                  <Card
                    key={index}
                    className={cn(
                      "border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900",
                      "animate-in fade-in slide-in-from-bottom-4 fill-mode-both"
                    )}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    <CardContent className="p-5 sm:p-6">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1 space-y-1.5">
                          <p className="text-xl font-bold text-slate-900 dark:text-white">
                            {getPhraseRomanization(phrase)}
                          </p>
                          <p className="text-lg text-slate-600 dark:text-slate-400">
                            {phrase.thaiText}
                          </p>
                          <p className="text-base italic text-slate-500 dark:text-slate-500">
                            {phrase.content}
                          </p>
                          {phrase.audioUrl && (
                            <div className="pt-2">
                              <AudioPlayer audioUrl={phrase.audioUrl} variant="dark" className="w-full max-w-sm px-3 py-2" />
                            </div>
                          )}
                        </div>
                        <Badge variant="outline" className="shrink-0 text-xs">
                          {phrase.level === "beginner" ? "Beginner" : "Intermediate"}
                        </Badge>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Related Words */}
          {relatedWords.length > 0 && (
            <div className="mb-10">
              <h2 className="mb-6 text-xl font-bold text-foreground">
                Related Words
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {relatedWords.slice(0, 4).map((related, index) => {
                  const content = (
                    <Card
                      className={cn(
                        "h-full border-slate-200 bg-white transition-all duration-300",
                        related.exists && "hover:-translate-y-1 hover:border-indigo-300 hover:shadow-lg hover:shadow-indigo-500/10",
                        "dark:border-slate-700 dark:bg-slate-900",
                        related.exists && "dark:hover:border-indigo-700",
                        "animate-in fade-in slide-in-from-bottom-4 fill-mode-both"
                      )}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <CardContent className="p-5">
                        {(related.romanizationPaiboon || related.romanizationRtgs) && (
                          <p className="mb-1 text-lg font-semibold text-foreground">
                            {romanization === "paiboon"
                              ? (related.romanizationPaiboon || related.romanizationRtgs)
                              : (related.romanizationRtgs || related.romanizationPaiboon)}
                          </p>
                        )}
                        {related.thaiScript && (
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {related.thaiScript}
                          </p>
                        )}
                        <p className={cn(
                          "mt-1 text-sm",
                          (related.romanizationPaiboon || related.romanizationRtgs)
                            ? "text-slate-500 dark:text-slate-400"
                            : "font-medium text-foreground"
                        )}>
                          {related.english}
                        </p>
                        {related.exists && (
                          <div className="mt-3 flex items-center gap-1 text-xs font-medium text-indigo-600 opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100 dark:text-indigo-400">
                            <span>Learn this word</span>
                            <ArrowRight className="size-3" />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )

                  if (related.exists) {
                    return (
                      <Link key={related.slug} href={`/words/${related.slug}`} className="group">
                        {content}
                      </Link>
                    )
                  }

                  return <div key={related.slug}>{content}</div>
                })}
              </div>
            </div>
          )}

          {/* CTA Section */}
          <Card className="relative overflow-hidden border-slate-200 dark:border-slate-700">
            <div className="pointer-events-none absolute -top-12 -right-12 size-48 rounded-full bg-indigo-500/10 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-12 size-48 rounded-full bg-violet-500/10 blur-3xl" />

            <CardContent className="relative bg-gradient-to-br from-slate-50 via-white to-indigo-50/50 p-8 text-center dark:from-slate-900 dark:via-slate-900 dark:to-indigo-950/30 sm:p-12">
              <div className="mx-auto mb-6 flex size-16 items-center justify-center rounded-2xl bg-indigo-100 dark:bg-indigo-900/50">
                <Sparkles className="size-8 text-indigo-600 dark:text-indigo-400" />
              </div>
              <h2 className="mb-4 text-2xl font-bold text-foreground sm:text-3xl">
                Practice {word.thaiScript} with flashcards
              </h2>
              <p className="mx-auto mb-8 max-w-xl text-slate-600 dark:text-slate-400">
                Master Thai vocabulary with AI-powered conversations and spaced repetition flashcards.
                Coming soon!
              </p>
              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <SharePopover
                  url={shareUrl}
                  title={`Learn "${word.english}" in Thai`}
                  description={`${word.thaiScript} — ${heroRomanization || word.english}`}
                />
                <Button asChild size="lg" className="gap-2 shadow-lg shadow-indigo-500/20">
                  <Link
                    href={auth.user ? "/dashboard" : "/sign_up"}
                    {...(auth.user ? dashboardPrefetch : signUpPrefetch)}
                  >
                    <Sparkles className="size-5" />
                    {auth.user ? "Go to Dashboard" : "Start Learning Free"}
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link href="/how-it-works" {...howItWorksPrefetch}>
                    See How It Works
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <MarketingFooter />
    </>
  )
}
